import React from 'react';
import LanguageSelector from './LanguageSelector';
import LogoMark from './LogoMark';
import HeroVisual from './HeroVisual';
import { translations } from '../content/translations';

const base = () => process.env.PUBLIC_URL || '';
const ctaTarget = () => `${base()}/s`;

export default function LandingPage({ language, onLanguageChange }) {
  const t = translations[language];
  const toFlow = ctaTarget();

  return (
    <div id="top" className="min-h-screen bg-[#f8fafc] text-slate-800 antialiased">
      {/* ——— Top bar ——— */}
      <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <a href="#top" className="flex items-center gap-2.5 shrink-0">
            <LogoMark className="h-9 w-9 text-[var(--df-accent)]" />
            <span className="font-display text-lg font-extrabold tracking-tight text-slate-900 sm:text-xl">
              DailyFit<span className="text-[var(--df-accent)]">24</span>
            </span>
          </a>
          <nav className="hidden items-center gap-8 text-sm font-medium text-slate-600 md:flex">
            <a href="#palvelu" className="transition hover:text-[var(--df-accent)]">
              {t.navProduct}
            </a>
            <a href="#ratkaisu" className="transition hover:text-[var(--df-accent)]">
              {t.navSolution}
            </a>
            <a href={toFlow} className="transition hover:text-[var(--df-accent)]">
              {t.navPricing}
            </a>
          </nav>
          <div className="flex items-center gap-3 sm:gap-4">
            <LanguageSelector
              language={language}
              onChange={onLanguageChange}
              labels={{ langFi: t.langFi, langEn: t.langEn }}
            />
            <a
              href={toFlow}
              className="hidden rounded-lg bg-[var(--df-accent)] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#d62f3c] sm:inline-block"
            >
              {t.navCta}
            </a>
          </div>
        </div>
      </header>

      {/* ——— Hero ——— */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-white">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_70%_-10%,rgba(230,57,70,0.06),transparent)]" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-20">
          <div>
            <p className="mb-4 inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-slate-600">
              {t.heroBadge}
            </p>
            <h1 className="font-display text-3xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]">
              {t.heroTitle}
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-600">{t.heroSubtitle}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={toFlow}
                className="inline-flex items-center justify-center rounded-xl bg-[var(--df-accent)] px-6 py-3.5 text-base font-semibold text-white shadow-lg shadow-red-500/25 transition hover:bg-[#d62f3c]"
              >
                {t.heroCta}
              </a>
              <a
                href={toFlow}
                className="inline-flex items-center justify-center rounded-xl border-2 border-slate-200 bg-white px-6 py-3.5 text-base font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                {t.heroSecondary}
              </a>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <div className="w-full max-w-[420px] rounded-3xl border border-slate-100 bg-gradient-to-br from-white to-slate-50 p-6 shadow-xl shadow-slate-200/60">
              <HeroVisual />
            </div>
          </div>
        </div>
      </section>

      {/* ——— Stats ——— */}
      <section className="border-b border-slate-200 bg-slate-50 py-10">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 sm:grid-cols-3 sm:px-6 lg:px-8">
          {t.stats.map((s) => (
            <div key={s.label} className="text-center sm:text-left">
              <p className="font-display text-3xl font-extrabold text-[var(--df-accent)] sm:text-4xl">{s.value}</p>
              <p className="mt-1 text-sm font-medium text-slate-600">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ——— Features ——— */}
      <section id="palvelu" className="scroll-mt-20 bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">{t.featuresTitle}</h2>
            <p className="mt-3 text-slate-600">{t.featuresSubtitle}</p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {t.features.map((f) => (
              <article
                key={f.title}
                className="rounded-2xl border border-slate-200 bg-[#fafbfc] p-6 transition hover:border-slate-300 hover:shadow-md"
              >
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--df-accent)]/10 text-[var(--df-accent)]">
                  <span className="text-lg font-bold">✓</span>
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ——— Split / Solution ——— */}
      <section id="ratkaisu" className="scroll-mt-20 border-y border-slate-200 bg-slate-50 py-16 sm:py-20">
        <div className="mx-auto grid max-w-6xl gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:items-center lg:gap-16 lg:px-8">
          <div>
            <h2 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">{t.splitTitle}</h2>
            <p className="mt-4 leading-relaxed text-slate-600">{t.splitLead}</p>
            <ul className="mt-8 space-y-4">
              {t.splitPoints.map((pt) => (
                <li key={pt} className="flex gap-3 text-slate-700">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--df-accent)] text-xs font-bold text-white">
                    ✓
                  </span>
                  {pt}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-lg shadow-slate-200/50">
            <p className="text-lg font-medium italic leading-relaxed text-slate-700">{t.quote}</p>
            <p className="mt-4 text-sm font-semibold text-[var(--df-accent)]">{t.quoteAuthor}</p>
          </div>
        </div>
      </section>

      {/* ——— Process ——— */}
      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">
            {t.processTitle}
          </h2>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {t.processSteps.map((s) => (
              <div key={s.step} className="relative rounded-2xl border border-slate-200 p-6 pt-10">
                <span className="absolute left-6 top-0 -translate-y-1/2 rounded-lg bg-[var(--df-accent)] px-2 py-1 text-xs font-bold text-white">
                  {s.step}
                </span>
                <h3 className="text-lg font-bold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ——— CTA + Legal ——— */}
      <section id="hinta" className="scroll-mt-20 bg-slate-900 py-16 text-white sm:py-20">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
          <h2 className="font-display text-2xl font-extrabold sm:text-3xl">{t.ctaBandTitle}</h2>
          <p className="mt-4 text-slate-300">{t.ctaBandText}</p>
          <a
            href={toFlow}
            className="mt-8 inline-flex rounded-xl bg-[var(--df-accent)] px-8 py-4 text-base font-semibold text-white transition hover:bg-[#ff4d5a]"
          >
            {t.cta}
          </a>
        </div>
      </section>

      <section id="tilaus" className="scroll-mt-20 bg-[#f8fafc] py-14 sm:py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <h2 className="font-display text-lg font-bold text-slate-900">
              {language === 'fi' ? 'Palvelun tiedot' : 'Service information'}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">{t.legalLead}</p>
            <p className="mt-4 text-sm font-medium text-slate-800">{t.legalPrice}</p>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">{t.legalOrder}</p>
            <p className="mt-4 text-sm text-slate-500">{t.legalProvider}</p>
          </div>
        </div>
      </section>

      {/* ——— Footer ——— */}
      <footer className="border-t border-slate-200 bg-white py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 px-4 sm:flex-row sm:px-6 lg:px-8">
          <div className="flex items-center gap-2">
            <LogoMark className="h-8 w-8 text-[var(--df-accent)]" />
            <span className="font-display font-bold text-slate-900">DailyFit24</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm font-semibold">
            <a href={`${base()}/terms.html`} className="text-[var(--df-accent)] hover:underline">
              {t.terms}
            </a>
            <a href={`${base()}/contact.html`} className="text-[var(--df-accent)] hover:underline">
              {t.contact}
            </a>
          </div>
          <p className="text-center text-xs text-slate-500 sm:text-right">{t.footerRights}</p>
        </div>
      </footer>
    </div>
  );
}
